import * as assert from 'assert-plus';
import { AxiosInstance } from 'axios';
import * as React from 'react';
import { withRouter } from 'react-router-dom';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import DataRow from 'src/components/DataRow';
import LogModal from 'src/components/LogModal';
import { logError } from 'src/utils/Error';
import 'src/assets/css/views/data.css';
import 'src/assets/css/components/pipelineType.css';
import dataIcon from '../assets/images/svgs/data.svg';
import pipelinesPlaceholder from '../components/placeholders/PipelinesPlaceholder';
import { APIContext } from '../contexts/api';
import { Auth0Context } from '../contexts/auth0';
import { MergedContexts } from '../contexts/merged';
import { WorkspaceContext } from '../contexts/workspace';
import { IPipelinesProps, IPipelinesState } from 'src/models/views/IPipelines';
import { AsyncLoader } from '../utils/async';
import history from 'src/utils/history';
import { bindContexts } from '../utils/bindContexts';
import ConfirmDialog from 'src/components/ConfirmDialog'
import IJob, { JobStatus } from 'src/models/IJob'
import { canDownloadLogs } from 'src/utils/jobStatus'
import IPipeline, { PipelineHealthStatus, PipelineType } from 'src/models/IPipeline'
import { isBackfill, pipelineType, pipelineTypeDescription, pipelineTypeExample, pipelineTypeLabel } from 'src/utils/pipelineType'
import { PipelineSearch } from 'src/components/Search'
import PipelineFilters, { IPipelineFilterState, matchesPipelineFilters, PipelineTrigger } from 'src/components/PipelineFilters'
import SplitButton from 'src/components/SplitButton'
import PipelineTypeIcon from 'src/components/PipelineTypeIcon'
import EntityRel from 'src/models/EntityRel'
import AddIcon from '@material-ui/icons/Add'
import Alert from '@material-ui/lab/Alert'
import ILog from 'src/models/ILog'

class Pipelines extends React.Component<IPipelinesProps, IPipelinesState> implements AsyncLoader {
    public isThisMounted = false;
    static contextType = MergedContexts;
    constructor(props: IPipelinesProps) {
        super(props);
        this.state = {
            viewClass: 'listView',
            open: false,
            taskId: 0,
            logsBySequence: {},
            isConfirmDialogOpen: false,
        };

        this.deletePipeline = this.deletePipeline.bind(this);
        this.openLog = this.openLog.bind(this);
        this.renderLogs = this.renderLogs.bind(this);
        this.hideLog = this.hideLog.bind(this);
        this.downloadLog = this.downloadLog.bind(this);
        this.handleDeletePipeline = this.handleDeletePipeline.bind(this);
        this.handleOpenPipelineConfirmDialog = this.handleOpenPipelineConfirmDialog.bind(this)
        this.handleClosePipelineConfirmDialog = this.handleClosePipelineConfirmDialog.bind(this)

    }

    // implements AsyncLoader
    public isLoading(): boolean {
        return this.context.isLoadingPipelines;
    }

    public componentDidMount() {
        this.isThisMounted = true;
        this.props.setViewName && this.props.setViewName("Data Imports")
        const { catalogAPI, workspace, fetchPipelines } = this.context;
        const api = catalogAPI as AxiosInstance;
        assert.func(api, 'APIContext must be initialise before this component is used');


        fetchPipelines(workspace)
    }

    public componentWillUnmount() {
        this.isThisMounted = false;
    }

    public deletePipeline(pipeline: IPipeline) {
        this.context.setPipelines((pipelines: IPipeline[]) => pipelines.filter(p => p.id !== pipeline.id))
    }

    public openLog(job: IJob) {
        this.setState({ open: true, job });
    }

    public renderLogs(logsArray: ILog[], taskId: any) {
        const logsBySequence = logsArray.reduce((obj, l) => ({ ...obj, [l.sequence]: l }), this.state.logsBySequence)
        this.setState({ logsBySequence, taskId });
    }

    public hideLog() {
        clearTimeout(this.state.taskId);
        this.setState({ open: false, logsBySequence: {}, taskId: null });
    }

    public async downloadLog(job: IJob) {
        if (!canDownloadLogs(job.status)) return
        const { catalogAPI } = this.context;
        await catalogAPI
            .get(job._links.logs.href)
            .then((resp: any) => {
                // a blob URL avoids the base64 memory overhead of a data: URI,
                // which matters now the full (unbounded) log is downloadable
                const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/plain' }));
                const link = document.createElementNS('http://www.w3.org/1999/xhtml', 'a') as HTMLAnchorElement
                link.href = url;
                link.target = '_blank';
                link.download = `${job.id}.txt`;
                link.click();
                URL.revokeObjectURL(url);
            })
            .catch((e: any) => {
                logError(e);
            });
    }

    public async handleDeletePipeline() {
        const pipeline = this.state.pipeline
        if (!pipeline) return

        const { catalogAPI } = this.context;
        await catalogAPI.delete(pipeline._links['delete pipeline'].href)
            .then(() => this.deletePipeline(pipeline))
            .catch(logError)
    }

    private handleOpenPipelineConfirmDialog(pipeline: IPipeline) {
        this.setState({ isConfirmDialogOpen: true, pipeline: pipeline })
    }
    private handleClosePipelineConfirmDialog() {
        this.setState({ isConfirmDialogOpen: false })
    }

    public render() {
        const { open, logsBySequence, job, taskId } = this.state;
        const { workspace } = this.context;
        assert.object(workspace, 'View must be used inside WorkspaceProvider');

        const onEdit = (pipeline: IPipeline) => history.push(`${location.pathname}/${pipeline.id}`)

        // the search and filters live in the query string, so Back from a pipeline returns to the same list
        const query = new URLSearchParams(this.props.location.search)
        const searchText = query.get('search') || ''
        const filters: IPipelineFilterState = {
            type: query.get('type')?.toUpperCase() as PipelineType,
            health: query.get('health')?.toUpperCase() as PipelineHealthStatus,
            lastRunStatus: query.get('lastRunStatus')?.toUpperCase() as JobStatus,
            trigger: query.get('trigger')?.toUpperCase() as PipelineTrigger,
            plugin: query.get('plugin') || undefined,
        }
        // replace, not push: Back leaves the list rather than undo one keystroke at a time
        const setQuery = (search: string, { plugin, ...enumFilters }: IPipelineFilterState) => {
            const params = new URLSearchParams()
            search && params.set('search', search)
            Object.entries(enumFilters).forEach(([key, value]) => value && params.set(key, value.toLowerCase()))
            // a plugin name is matched exactly, so it keeps its case
            plugin && params.set('plugin', plugin)
            this.props.history.replace({ search: params.toString() })
        }

        // a backfill shows in a tab of the pipeline that it backfills
        const pipelines = this.context.pipelines.filter((p: IPipeline) => !isBackfill(p))
        const isDeletingBackfill = !!this.state.pipeline && isBackfill(this.state.pipeline)
        return (
            <>
                {this.state.pipeline &&
                    <ConfirmDialog
                        isOpen={this.state.isConfirmDialogOpen}
                        title={isDeletingBackfill ? "Delete backfill?" : `Delete '${this.state.pipeline.label || this.state.pipeline.name}'?`}
                        message={`Remove this ${isDeletingBackfill ? 'backfill'
                            : pipelineType(this.state.pipeline) === PipelineType.Ingestion ? 'import' : 'workflow'} and its run history. This action cannot be undone.`}
                        confirmLabel="Delete"
                        handleClose={this.handleClosePipelineConfirmDialog}
                        handleConfirm={this.handleDeletePipeline} />}
                <div className='rightside_pageContent data-page'>
                    {job &&
                        <LogModal
                            show={open}
                            logArray={Object.values(logsBySequence)}
                            job={job}
                            hideLog={this.hideLog}
                            downloadLog={this.downloadLog}
                            taskId={taskId}
                        />}
                    {workspace.disablePipelineSchedules &&
                        <Alert data-testid="pipeline-schedules-disabled" severity="info" style={{ marginBottom: 16 }}>
                            Scheduled runs are off in this workspace
                            {workspace.defaultEnvironment ? ` (${workspace.defaultEnvironment})` : ''}.
                            Pipelines run when started by hand or by another pipeline.
                            Set by <code>disable_pipeline_schedules</code> in{' '}
                            <code>workspace-{workspace.defaultEnvironment || '<environment>'}.yml</code>.
                        </Alert>}
                    {!this.context.isLoadingPipelines ?
                        <PipelineSearch
                            entities={pipelines}
                            initialValue={searchText}
                            onValueChanged={search => setQuery(search, filters)}
                            toolbar={
                                <PipelineFilters
                                    pipelines={pipelines}
                                    filters={filters}
                                    onChange={(f: IPipelineFilterState) => setQuery(searchText, f)} />}
                            filter={(p: IPipeline) => matchesPipelineFilters(p, filters)}
                            noMatches={
                                <div className="pipelines-no-matches">
                                    No pipelines match the current filters.
                                </div>}
                            new={<>{workspace._links?.['new pipeline'] &&
                                <SplitButton
                                    icon={<AddIcon />}
                                    menuHeader='What should this pipeline do? To do something different later, you can create another pipeline.'
                                    options={[PipelineType.Ingestion, PipelineType.Workflow].map(type => ({
                                        label: pipelineTypeLabel(type),
                                        description: pipelineTypeDescription(type),
                                        example: pipelineTypeExample(type),
                                        chip: <span className={`pipeline-type-chip ${type.toLowerCase()}`} aria-hidden>{pipelineTypeLabel(type)}</span>,
                                        menuIcon: <PipelineTypeIcon type={type} />,
                                        onClick: () => history.push(`/lab/${workspace.id}/${EntityRel.Pipeline.collection}/new?type=${type}`)
                                    }))} />
                                }</>}
                            row={e =>
                                <DataRow
                                    key={e.id}
                                    pipeline={e}
                                    renderLogs={this.renderLogs}
                                    openLog={this.openLog}
                                    downloadLog={this.downloadLog}
                                    handleOpenPipelineConfirmDialog={this.handleOpenPipelineConfirmDialog}
                                    onEdit={onEdit} />}
                            noEntities={
                                <div className="no-datasources content-center">
                                    <img
                                        alt="Data sources"
                                        src={dataIcon}
                                    />
                                    <h1>No data sources yet. Feed me some data!</h1>
                                </div>} /> :
                        pipelinesPlaceholder}
                </div>
            </>
        );
    }
}

export const PipelinesWithContext = bindContexts(Pipelines, [Auth0Context, APIContext, WorkspaceContext]);

export default withRouter(PipelinesWithContext);
